// react-native-maps has no web build, so it used to be aliased to the null
// `noop` shim → every map screen (Live DB Map, Routing "Map" sheet, OrdersMapView,
// LiveDbTrail, Location pickers) rendered an empty grey box on web. This shim
// renders a real, interactive map with Leaflet + OpenStreetMap tiles (no API
// key needed) and implements the subset of the react-native-maps API the app
// uses: MapView (initialRegion/region, onPress, onMapReady,
// onRegionChange(Complete), min/maxZoomLevel, and the ref methods
// animateToRegion / fitToCoordinates / getCamera / animateCamera / setCamera),
// Marker (coordinate, anchor, onPress, title/description, pinColor, zIndex and
// custom children — portalled into the marker element, so the app's own marker
// Views render as-is), Polyline, Polygon, Circle, Callout.
// Leaflet is loaded at runtime from the CDN (pinned + SRI), so no new npm
// dependency and no change to the native bundle. Aliased via
// react-native-maps$ in webpack.web.config.js.
import React, {
  Children,
  Component,
  createContext,
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { View, Text, StyleSheet } from "react-native";

const LEAFLET = "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet";
const LEAFLET_JS_SRI = "sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=";
const LEAFLET_CSS_SRI = "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=";
const TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

let leafletPromise = null;
function loadLeaflet() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("no window"));
  }
  if (window.L && window.L.map) {
    return Promise.resolve(window.L);
  }
  if (leafletPromise) {
    return leafletPromise;
  }
  leafletPromise = new Promise((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `${LEAFLET}.css`;
    css.integrity = LEAFLET_CSS_SRI;
    css.crossOrigin = "";
    document.head.appendChild(css);
    const script = document.createElement("script");
    script.src = `${LEAFLET}.js`;
    script.integrity = LEAFLET_JS_SRI;
    script.crossOrigin = "";
    script.onload = () => resolve(window.L);
    script.onerror = () => {
      leafletPromise = null;
      reject(new Error("Leaflet failed to load"));
    };
    document.head.appendChild(script);
  });
  return leafletPromise;
}

const MapContext = createContext(null);

const toLatLng = (c) => [Number(c.latitude), Number(c.longitude)];
const isValidCoord = (c) =>
  !!c &&
  Number.isFinite(Number(c.latitude)) &&
  Number.isFinite(Number(c.longitude));

function regionToBounds(L, r) {
  const lat = Number(r.latitude);
  const lng = Number(r.longitude);
  const dLat = Math.abs(Number(r.latitudeDelta) || 0.02) / 2;
  const dLng = Math.abs(Number(r.longitudeDelta) || 0.02) / 2;
  return L.latLngBounds([lat - dLat, lng - dLng], [lat + dLat, lng + dLng]);
}

function boundsToRegion(map) {
  const b = map.getBounds();
  const c = map.getCenter();
  return {
    latitude: c.lat,
    longitude: c.lng,
    latitudeDelta: b.getNorth() - b.getSouth(),
    longitudeDelta: b.getEast() - b.getWest(),
  };
}

class MapView extends Component {
  constructor(props) {
    super(props);
    this.state = { map: null, L: null, failed: false };
    this.containerRef = createRef();
    this.pendingView = null;
  }

  componentDidMount() {
    this.mounted = true;
    loadLeaflet()
      .then((L) => this.mounted && this.createMap(L))
      .catch(() => this.mounted && this.setState({ failed: true }));
  }

  componentDidUpdate(prevProps) {
    const { region } = this.props;
    if (
      this.state.map &&
      region &&
      isValidCoord(region) &&
      JSON.stringify(region) !== JSON.stringify(prevProps.region)
    ) {
      this.showRegion(region, false);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.state.map) {
      this.state.map.remove();
    }
  }

  createMap(L) {
    const el = this.containerRef.current;
    if (!el) {
      return;
    }
    const { minZoomLevel, maxZoomLevel, scrollEnabled, zoomEnabled } =
      this.props;
    const map = L.map(el, {
      zoomControl: false,
      minZoom: minZoomLevel || 2,
      maxZoom: Math.min(maxZoomLevel || 19, 19),
      dragging: scrollEnabled !== false,
      scrollWheelZoom: zoomEnabled !== false,
      doubleClickZoom: zoomEnabled !== false,
      touchZoom: zoomEnabled !== false,
    });
    L.tileLayer(TILE_URL, {
      maxZoom: 19,
      attribution: TILE_ATTRIBUTION,
    }).addTo(map);
    const start = this.props.region || this.props.initialRegion;
    if (isValidCoord(start)) {
      map.fitBounds(regionToBounds(L, start), { animate: false });
    } else {
      map.setView([20.5937, 78.9629], 4);
    }
    map.on("click", (e) => {
      if (this.props.onPress) {
        this.props.onPress({
          nativeEvent: {
            coordinate: { latitude: e.latlng.lat, longitude: e.latlng.lng },
          },
        });
      }
    });
    map.on("move", () => {
      if (this.props.onRegionChange) {
        this.props.onRegionChange(boundsToRegion(map));
      }
    });
    map.on("moveend", () => {
      if (this.props.onRegionChangeComplete) {
        this.props.onRegionChangeComplete(boundsToRegion(map));
      }
    });
    // Modals / tabs mount the map before layout → size 0 at init. Re-measure
    // on every resize and replay any view requested while it had no size.
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        map.invalidateSize();
        if (this.pendingView && el.clientWidth && el.clientHeight) {
          const run = this.pendingView;
          this.pendingView = null;
          run();
        }
      });
      this.resizeObserver.observe(el);
    }
    this.setState({ map, L }, () => {
      if (this.props.onMapReady) {
        this.props.onMapReady();
      }
    });
  }

  runWhenSized(fn) {
    const el = this.containerRef.current;
    if (el && el.clientWidth && el.clientHeight) {
      fn();
    } else {
      this.pendingView = fn;
    }
  }

  showRegion(region, animated) {
    const { map, L } = this.state;
    if (!map || !isValidCoord(region)) {
      return;
    }
    this.runWhenSized(() =>
      map.fitBounds(regionToBounds(L, region), { animate: !!animated }),
    );
  }

  // ---- react-native-maps ref API -------------------------------------------
  animateToRegion(region, duration) {
    this.showRegion(region, duration !== 0);
  }

  fitToCoordinates(coordinates = [], options = {}) {
    const { map, L } = this.state;
    const pts = coordinates.filter(isValidCoord).map(toLatLng);
    if (!map || !pts.length) {
      return;
    }
    const p = options.edgePadding || {};
    this.runWhenSized(() =>
      map.fitBounds(L.latLngBounds(pts), {
        paddingTopLeft: [p.left || 0, p.top || 0],
        paddingBottomRight: [p.right || 0, p.bottom || 0],
        animate: options.animated !== false,
      }),
    );
  }

  getCamera() {
    const { map } = this.state;
    if (!map) {
      return Promise.reject(new Error("map not ready"));
    }
    const c = map.getCenter();
    return Promise.resolve({
      center: { latitude: c.lat, longitude: c.lng },
      zoom: map.getZoom(),
      heading: 0,
      pitch: 0,
      altitude: 0,
    });
  }

  animateCamera(camera = {}, opts = {}) {
    const { map } = this.state;
    if (!map) {
      return;
    }
    const center = isValidCoord(camera.center)
      ? toLatLng(camera.center)
      : map.getCenter();
    const zoom = Number.isFinite(camera.zoom) ? camera.zoom : map.getZoom();
    map.setView(center, zoom, { animate: opts.duration !== 0 });
  }

  setCamera(camera) {
    this.animateCamera(camera, { duration: 0 });
  }

  animateToCoordinate(coordinate) {
    this.animateCamera({ center: coordinate });
  }
  // ---------------------------------------------------------------------------

  render() {
    const { style, children, onLayout, testID } = this.props;
    const { map, L, failed } = this.state;
    return (
      <View style={[styles.root, style]} onLayout={onLayout} testID={testID}>
        <View ref={this.containerRef} style={StyleSheet.absoluteFill} />
        {failed ? (
          <View style={[StyleSheet.absoluteFill, styles.failed]}>
            <Text style={styles.failedText}>Map could not be loaded</Text>
          </View>
        ) : null}
        {map ? (
          <MapContext.Provider value={{ map, L }}>
            {children}
          </MapContext.Provider>
        ) : null}
      </View>
    );
  }
}

function useLatest(value) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

function DefaultPin({ color }) {
  return (
    <svg width="26" height="38" viewBox="0 0 26 38">
      <path
        d="M13 0C5.8 0 0 5.8 0 13c0 9.8 13 25 13 25s13-15.2 13-25C26 5.8 20.2 0 13 0z"
        fill={color || "#EA4335"}
        stroke="#fff"
        strokeWidth="1.5"
      />
      <circle cx="13" cy="13" r="4.5" fill="#fff" />
    </svg>
  );
}

function Marker(props) {
  const ctx = useContext(MapContext);
  const latest = useLatest(props);
  const markerRef = useRef(null);
  const [host, setHost] = useState(null);
  const { coordinate, anchor, zIndex, title, description } = props;
  const hasCoord = isValidCoord(coordinate);

  useEffect(() => {
    if (!ctx || !hasCoord) {
      return undefined;
    }
    const { map, L } = ctx;
    const icon = L.divIcon({ className: "", html: "", iconSize: [0, 0] });
    const marker = L.marker(toLatLng(coordinate), { icon }).addTo(map);
    marker.on("click", () => {
      const p = latest.current;
      if (p.onPress) {
        p.onPress({
          nativeEvent: { coordinate: p.coordinate, id: p.identifier },
        });
      }
    });
    const wrapper = document.createElement("div");
    wrapper.style.position = "absolute";
    wrapper.style.left = "0";
    wrapper.style.top = "0";
    // the icon element is 0x0 → size the host to its content, not to 0
    wrapper.style.width = "max-content";
    marker.getElement().appendChild(wrapper);
    markerRef.current = marker;
    setHost(wrapper);
    return () => {
      marker.remove();
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, hasCoord]);

  useEffect(() => {
    if (markerRef.current && hasCoord) {
      markerRef.current.setLatLng(toLatLng(coordinate));
    }
  }, [coordinate, hasCoord]);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setZIndexOffset((Number(zIndex) || 0) * 1000);
    }
  }, [zIndex, host]);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) {
      return;
    }
    marker.unbindPopup();
    if (title || description) {
      marker.bindPopup([title, description].filter(Boolean).join("<br/>"));
    }
  }, [title, description, host]);

  if (!host) {
    return null;
  }
  // react-native-maps anchors custom views at bottom-centre by default.
  const ax = anchor && Number.isFinite(anchor.x) ? anchor.x : 0.5;
  const ay = anchor && Number.isFinite(anchor.y) ? anchor.y : 1;
  host.style.transform = `translate(${-ax * 100}%, ${-ay * 100}%)`;
  const content = Children.toArray(props.children).some(
    (c) => c && c.type !== Callout,
  ) ? (
    props.children
  ) : (
    <DefaultPin color={props.pinColor} />
  );
  return createPortal(content, host);
}

function useShape(create, deps) {
  const ctx = useContext(MapContext);
  useEffect(() => {
    if (!ctx) {
      return undefined;
    }
    const layer = create(ctx.L);
    if (!layer) {
      return undefined;
    }
    layer.addTo(ctx.map);
    return () => layer.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, ...deps]);
}

function strokeOptions(p) {
  return {
    color: p.strokeColor || "#000",
    weight: p.strokeWidth || 1,
    dashArray: Array.isArray(p.lineDashPattern)
      ? p.lineDashPattern.join(",")
      : undefined,
  };
}

function Polyline(props) {
  const coords = (props.coordinates || []).filter(isValidCoord).map(toLatLng);
  useShape(
    (L) =>
      coords.length > 1 ? L.polyline(coords, strokeOptions(props)) : null,
    [JSON.stringify(coords), props.strokeColor, props.strokeWidth],
  );
  return null;
}

function Polygon(props) {
  const coords = (props.coordinates || []).filter(isValidCoord).map(toLatLng);
  useShape(
    (L) =>
      coords.length > 2
        ? L.polygon(coords, {
            ...strokeOptions(props),
            fillColor: props.fillColor || "transparent",
            fillOpacity: 1,
          })
        : null,
    [JSON.stringify(coords), props.strokeColor, props.fillColor],
  );
  return null;
}

function Circle(props) {
  const { center, radius } = props;
  const valid = isValidCoord(center) && Number(radius) > 0;
  useShape(
    (L) =>
      valid
        ? L.circle(toLatLng(center), {
            ...strokeOptions(props),
            radius: Number(radius),
            fillColor: props.fillColor || "transparent",
            fillOpacity: 1,
          })
        : null,
    [valid && center.latitude, valid && center.longitude, radius],
  );
  return null;
}

// Native callouts are info-windows; on web the marker's title popup covers it.
function Callout() {
  return null;
}

const Noop = () => null;

const MAP_BG = "#e5e3df";
const FAILED_TEXT = "#555";

const styles = StyleSheet.create({
  root: { overflow: "hidden", backgroundColor: MAP_BG },
  failed: { alignItems: "center", justifyContent: "center" },
  failedText: { color: FAILED_TEXT, fontSize: 13 },
});

export {
  MapView,
  Marker,
  Marker as MapMarker,
  Polyline,
  Polyline as MapPolyline,
  Polygon,
  Polygon as MapPolygon,
  Circle,
  Circle as MapCircle,
  Callout,
  Callout as MapCallout,
  Noop as Overlay,
  Noop as Heatmap,
  Noop as UrlTile,
  Noop as LocalTile,
};
export const PROVIDER_GOOGLE = "google";
export const PROVIDER_DEFAULT = undefined;
export default MapView;
